import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Badge, Box, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useContext } from 'react';
import { SidebarContext } from '../../contexts/SidebarContext';
import navigationConfig from '../../config/navigation';
import { apiStatusColor } from 'constants/apiStatusBadge';
import { newChatNavState } from 'components/travelApp/newChatSignal';
import { colors, typography } from 'theme/tokens';

// Every path the sidebar links to, so an item can tell whether a MORE specific
// sibling also matches the current route.
const ALL_NAV_PATHS = navigationConfig.flatMap((group) =>
  group.items.flatMap((item) => (item.children ? item.children.map((child) => child.path) : item.path ? [item.path] : [])),
);

const matchesPath = (pathname, navPath) =>
  navPath === '/admin' ? pathname === navPath : pathname === navPath || pathname.startsWith(`${navPath}/`);

/**
 * Longest match wins. `/admin/bookings/manual-flight` prefix-matches BOTH
 * Flight Manual Form and Booking Management (`/admin/bookings`), which is why
 * NavLink's own prefix matching lit up two items at once. An item is active
 * only when no nav path nested underneath it also matches — so a route with no
 * sidebar entry of its own (a booking detail page, say) still highlights its
 * closest parent, which plain `end` matching would have dropped.
 */
const isPathActive = (pathname, path) =>
  matchesPath(pathname, path) &&
  !ALL_NAV_PATHS.some((other) => other.startsWith(`${path}/`) && matchesPath(pathname, other));

export default function SidebarNavItem({ label, path, icon: Icon, nested, apiStatus, newChat }) {
  const { collapsed, closeMobile } = useContext(SidebarContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Own active state rather than NavLink's `.active` class (which is prefix-only
  // and cannot express the rule above). The class is still added by NavLink; it
  // just carries no styling now.
  const active = isPathActive(pathname, path);
  // QA-only status dot: green = APIs verified working, yellow = in progress.
  // Returns undefined when SHOW_API_STATUS_BADGE (flagSwitch/SwitchConfig) is
  // off, which hides it for everyone without touching the per-item `apiStatus`
  // in navigation.js.
  const dotColor = apiStatusColor(apiStatus);

  /**
   * SMB-4110 — "start a FRESH conversation", as distinct from "go to chat".
   *
   * Navigating to the same path is a no-op for the embedded traveller app, so
   * the reset cannot be expressed as a link; it has to be a signal the target
   * screen can tell apart from arriving normally. That signal is a nonce in
   * react-router's location state (see `newChatSignal.js`) — invisible in the
   * URL, new on every click, so even clicking this while already on Start Chat
   * produces a location the page has not rendered yet.
   *
   * `preventDefault` because this sits INSIDE the NavLink: without it the
   * anchor navigates too, landing on the same path with no state and racing
   * the state-carrying navigation below.
   *
   * Hidden while collapsed — a 72px rail has room for the icon or the action,
   * not both, and the item's own glyph has to win.
   */
  const handleNewChat = (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeMobile?.();
    navigate(path, { state: newChatNavState() });
  };

  return (
    <Tooltip title={collapsed ? label : ''} placement="right" arrow>
      <ListItemButton
        component={NavLink}
        to={path}
        end={path === '/admin'}
        onClick={closeMobile}
        sx={{
          mx: 1,
          mb: 0.5,
          borderRadius: '10px',
          color: colors.sidebar.text,
          minHeight: nested ? 42 : 46,
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 1.5 : 2,
          transition: 'all 0.15s ease',
          position: 'relative',
          ...(active && {
            backgroundColor: colors.sidebar.activeBg,
            color: colors.sidebar.textActive,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '20%',
              bottom: '20%',
              width: 3,
              borderRadius: '0 3px 3px 0',
              backgroundColor: colors.sidebar.accent,
            },
            '& .MuiListItemIcon-root': {
              color: colors.sidebar.iconActive,
            },
          }),
          '&:hover': {
            backgroundColor: colors.sidebar.hoverBg,
            color: colors.sidebar.textHover,
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 36,
            color: 'inherit',
            justifyContent: 'center',
          }}
        >
          <Badge
            variant="dot"
            invisible={!dotColor}
            sx={{ '& .MuiBadge-dot': { backgroundColor: dotColor, boxShadow: `0 0 0 2px ${colors.sidebar.ring}` } }}
          >
            <Icon sx={{ fontSize: nested ? typography.fontSize[20] : typography.fontSize[22] }} />
          </Badge>
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontSize: typography.fontSize[14],
              fontFamily: typography.fontFamily.inter,
              fontWeight: typography.fontWeight.medium,
              letterSpacing: '-0.01em',
            }}
          />
        )}
        {newChat && !collapsed && (
          <Tooltip title="Start a new chat" placement="right" arrow>
            {/*
              A SPAN, NOT AN IconButton. This lives inside a `ListItemButton
              component={NavLink}`, which renders an <a>, and a <button> nested
              in an anchor is invalid HTML — React will mount it but the two
              compete for the same click and screen readers get an unresolvable
              control. A span with an explicit role and key handling is the
              same affordance without the nesting.

              AT REST it looks like the group's expand arrow, and is styled
              from the same two values: `color: inherit` at `opacity: 0.6`,
              exactly what `ExpandMore`/`ExpandLess` carry on the parent rows
              in Sidebar.jsx. Inheriting rather than fixing a hex is what makes
              it track the row — #A990C8 at rest, #E8D5FF while the row is
              hovered, #F0E6FF on the active item.

              ON ITS OWN HOVER it goes WHITE, which is the one thing it does
              not share with those arrows. The arrows only ever disclose; this
              destroys a conversation, so at the moment the pointer is on it —
              and not merely on the row — it stops blending in and states
              plainly that the click is going to land here rather than on the
              link underneath.

              It went through a filled yellow disc first. That read as a
              second, competing button: nothing in this sidebar carries a solid
              background except the ACTIVE item, so the disc claimed a weight
              this control does not have. White ink says the same thing without
              the weight.
            */}
            <Box
              component="span"
              role="button"
              tabIndex={0}
              aria-label="Start a new chat"
              onClick={handleNewChat}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') handleNewChat(event);
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                /* 28, not 24: the glyph inside is 22px, and a 24px box left
                   it a single pixel of room on each side — the rounded focus
                   ring and the quarter-turn both clipped against it. */
                width: 28,
                height: 28,
                ml: 1,
                flexShrink: 0,
                borderRadius: '50%',
                cursor: 'pointer',
                color: 'inherit',
                opacity: 0.6,
                /* The glyph turns on ITS OWN hover — not the row's. It is the
                   one control on the row that does something different from
                   the row, so it has to answer the pointer separately or
                   there is no way to tell you are about to hit it rather than
                   the link under it. The partner twin does the same thing
                   from a local hover state, since inline styles cannot
                   express `:hover`. */
                transition: 'opacity 0.15s ease, color 0.15s ease, transform 0.2s ease',
                '&:hover': { opacity: 1, color: colors.sidebar.actionHover, transform: 'rotate(-90deg)' },
                '&:active': { transform: 'rotate(-180deg)' },
                '&:focus-visible': { opacity: 1, color: colors.sidebar.actionHover, outline: '2px solid currentColor', outlineOffset: 2 },
              }}
            >
              <RefreshIcon sx={{ fontSize: typography.fontSize[22] }} />
            </Box>
          </Tooltip>
        )}
      </ListItemButton>
    </Tooltip>
  );
}
